import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { api, isNotFound } from "@/lib/api";
import { islandUrl, tipsUrl, tr, ISLAND_BY_PATH } from "@/lib/translations";
import NotFound from "@/pages/NotFound";
import { LoadError } from "@/components/LoadError";
import { Markdown } from "@/components/Markdown";
import PropertyCard from "@/components/PropertyCard";
import { useSeo } from "@/lib/useSeo";
import { AdminEditButton } from "@/components/AdminEditButton";
import { useTextVars, applyVars } from "@/lib/textVars";
import { TipCard } from "@/components/TipCard";
import { ShareButtons } from "@/components/ShareButtons";
import { TOPIC_ICONS, topicLabel } from "@/lib/tipTopics";
import { MapPin } from "lucide-react";

export default function CmsPage({ slug: slugProp }) {
  const params = useParams();
  const slug = (slugProp || params.place || params.path || "").replace(/\.html$/, "");
  const island = params.place ? ISLAND_BY_PATH[params.path] : undefined;
  const { lang, t } = useLang();
  const [page, setPage] = useState(null);
  const [missing, setMissing] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setMissing(false); setPage(null);
    api.get(`/pages/${slug}`, { params: { island } }).then((r) => {
      setPage(r.data);
      const key = `tipview:${r.data.island || ""}/${r.data.slug}`;
      if (r.data.kind === "tip" && !sessionStorage.getItem(key)) { sessionStorage.setItem(key, "1"); api.post(`/pages/${r.data.slug}/view`, null, { params: { island: r.data.island || undefined } }).catch(() => {}); }
    }).catch((e) => (isNotFound(e) ? setMissing(true) : setFailed(true)));
    window.scrollTo(0, 0);
  }, [slug, island]);

  const vars = useTextVars({ island });
  const c0 = page ? (page.content[lang]?.body ? page.content[lang] : page.content.de) : null;
  const c = c0 ? { ...c0, body: applyVars(c0.body, vars), seo_title: applyVars(c0.seo_title, vars), meta_description: applyVars(c0.meta_description, vars) } : null;
  const title = page ? applyVars(tr(page.title, lang), vars) : "";
  useSeo({ title: c?.seo_title || title || "privateHOLIDAY", description: c?.meta_description || "", image: page?.hero_image, lang });

  if (failed) return <LoadError />;
  if (missing) return <NotFound island={island} />;
  if (!page) return <div className="max-w-7xl mx-auto px-6 py-32 min-h-screen text-center text-stone-500">{t.property.loading}</div>;
  const islandName = page.island_name?.[lang];
  const isTip = page.kind === "tip";
  const TopicIcon = isTip ? TOPIC_ICONS[page.topic] : null;
  const badges = isTip && (islandName || TopicIcon) ? (
    <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="cms-hero-badges">
      {islandName && <Link to={islandUrl(page.island)} className="inline-flex items-center gap-1.5 rounded-full bg-white/90 text-stone-800 text-[11px] uppercase tracking-wider font-semibold px-3 py-1 hover:bg-white" data-testid="cms-hero-island"><MapPin className="w-3.5 h-3.5 text-brand" />{islandName}</Link>}
      {TopicIcon && <Link to={`${tipsUrl()}?${page.island ? `island=${page.island}&` : ""}topic=${page.topic}`} className="inline-flex items-center gap-1.5 rounded-full bg-brand/90 text-white text-[11px] uppercase tracking-wider font-semibold px-3 py-1 hover:bg-brand" data-testid="cms-hero-topic"><TopicIcon className="w-3.5 h-3.5" />{topicLabel(t, page.topic)}</Link>}
    </div>
  ) : null;
  const filtered = !!(page.property_type || page.property_amenities?.length);
  const listParams = new URLSearchParams({ ...(page.island ? { island: page.island } : {}), ...(page.property_type ? { type: page.property_type } : {}), ...(page.property_amenities?.length ? { amenities: page.property_amenities.join(",") } : {}) }).toString();

  return (
    <article data-testid="cms-page">
      <AdminEditButton kind={page.kind === "tip" ? "tip" : "page"} id={page.id || page.slug} />
      {(page.hero_image || page.hero_fallback?.url) ? (
        <section className="relative min-h-[50vh] flex items-end overflow-hidden">
          <img src={page.hero_image || page.hero_fallback.url} alt={(page.hero_image ? tr(page.hero_alt, lang) : tr(page.hero_fallback.alt, lang)) || title} style={page.hero_image ? undefined : { objectPosition: page.hero_fallback.position?.replace("-", " ") }} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 hero-gradient-overlay" />
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pb-14 pt-32 w-full">
            <Crumbs t={t} page={page} islandName={islandName} title={title} light />
            {badges}
            <h1 className="font-serif text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.1] max-w-4xl" data-testid="cms-h1">{title}</h1>
          </div>
        </section>
      ) : (
        <section className="max-w-4xl mx-auto px-6 lg:px-10 pt-16">
          <Crumbs t={t} page={page} islandName={islandName} title={title} />
          {badges}
          <h1 className="font-serif text-stone-900 text-4xl sm:text-5xl lg:text-6xl leading-[1.1]" data-testid="cms-h1">{title}</h1>
        </section>
      )}

      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-14 text-base md:text-lg text-stone-700 leading-relaxed" data-testid="cms-body">
        <Markdown text={c.body} />
        {isTip && <div className="mt-10 pt-8 border-t border-stone-200"><ShareButtons title={title} testId="cms-share" /></div>}
      </section>

      {page.properties?.length > 0 ? (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16" data-testid="cms-properties">
          <div className="flex items-end justify-between gap-4 mb-8">
            <h2 className="font-serif text-3xl md:text-4xl text-stone-900">{filtered ? title : t.island.properties.replace("{n}", islandName)}</h2>
            {filtered
              ? <Link to={`/properties?${listParams}`} className="text-sm text-brand underline underline-offset-4 whitespace-nowrap" data-testid="cms-all-properties">{t.properties?.title || "Alle Ferienobjekte"} ({page.properties.length}) →</Link>
              : <Link to={islandUrl(page.island)} className="text-sm text-brand underline underline-offset-4">{t.island.backToIsland.replace("{n}", islandName)}</Link>}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{page.properties.slice(0, filtered ? 12 : 6).map((p) => <PropertyCard key={p.id} property={p} />)}</div>
        </section>
      ) : filtered && (
        <section className="max-w-4xl mx-auto px-6 lg:px-10 pb-16" data-testid="cms-properties-empty">
          <Link to={`/properties${page.island ? `?island=${page.island}` : ""}`} className="inline-flex items-center rounded-full bg-brand text-white px-6 py-3 text-sm font-semibold hover:opacity-90" data-testid="cms-all-properties">{t.properties?.title || "Alle Ferienobjekte"} →</Link>
        </section>
      )}

      {page.related?.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20 border-t border-stone-200 pt-14" data-testid="cms-related">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-brand">{t.tips.relatedSub}</div>
              <h2 className="font-serif text-3xl md:text-4xl text-stone-900 mt-3">{t.tips.related}</h2>
            </div>
            <Link to={`${tipsUrl()}${page.island ? `?island=${page.island}` : ""}`} className="text-sm text-brand font-semibold hover:underline underline-offset-4 whitespace-nowrap" data-testid="cms-related-all">{t.tips.all} →</Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {page.related.map((p) => <TipCard key={`${p.island || "x"}-${p.slug}`} tip={p} showIsland={p.island !== page.island} testId={`cms-related-${p.slug}`} />)}
          </div>
        </section>
      )}

    </article>
  );
}

const Crumbs = ({ t, page, islandName, title, light }) => (
  <nav className={`text-xs mb-3 flex gap-2 ${light ? "text-white/80" : "text-stone-500"}`} data-testid="cms-breadcrumb">
    <Link to="/" className="hover:underline">{t.nav.home}</Link>
    {page.kind === "tip" && <><span>/</span><Link to={tipsUrl()} className="hover:underline" data-testid="cms-breadcrumb-tips">{t.tips.breadcrumb}</Link></>}
    {islandName && <><span>/</span><Link to={islandUrl(page.island)} className="hover:underline">{islandName}</Link></>}
    <span>/</span><span>{title}</span>
  </nav>
);
